import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { Calendar, User, Clock, ArrowLeft, Share2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const BlogPost = () => {
  const { id } = useParams();
  const { toast } = useToast();

  // Sample blog posts data - In a real app, this would come from an API
  const blogPosts = [
    {
      id: "1",
      type: "blog",
      title: "Why Adaptive Reuse Is Shaping the Future of Hospitality in Downtown Cairo",
      subtitle: "A City That Never Sleeps",
      excerpt: "Few places in the world pulse with as much energy, history, and contrast as Downtown Cairo. Exploring how heritage buildings are reimagined as living, breathing spaces for a new generation of residents and travelers.",
      content: `
        <h2 class="subtitle-header">A City That Never Sleeps</h2>
        
        <p>Few places in the world pulse with as much energy, history, and contrast as Downtown Cairo. Once envisioned by Khedive Ismail as a "Paris on the Nile," its boulevards, Belle Époque buildings, and vibrant cultural institutions tell the story of a city that has always been both ancient and modern. Today, Downtown is experiencing a renaissance, a revival driven by adaptive reuse, where heritage buildings are reimagined as living, breathing spaces for a new generation of residents and travelers.</p>

        <h2>The Case for Adaptive Reuse</h2>
        
        <p>Globally, adaptive reuse has become a powerful trend in hospitality and real estate. Instead of demolishing and rebuilding, developers breathe new life into existing assets, preserving their character while updating them for modern use. In Downtown Cairo, this approach makes sense: the district's historic architecture is too valuable to lose, yet too often underutilized. Adaptive reuse bridges the gap, aligning sustainability with profitability.</p>

        <ul>
          <li>Prime locations already embedded in cultural and business districts.</li>
          <li>Lower capital costs compared to ground-up construction.</li>
          <li>Authentic experiences that connect guests to the soul of the city.</li>
        </ul>

        <h2>Downtown Cairo Through a Hospitality Lens</h2>
        
        <p>For travelers, Downtown offers proximity to Cairo's most iconic landmarks including the Egyptian Museum, Tahrir Square, the Nile Corniche, and the Opera House, while also serving as a hub for dining, nightlife, and business. Yet for decades, the district lacked modern accommodation options that matched its potential.</p>
        
        <p>That is where adaptive reuse comes in. Brassbell Hospitality Group (BHG) has been at the forefront of converting historic and underutilized properties into contemporary serviced residences and boutique stays. These projects do not just provide a place to sleep; they immerse guests in the living history of the city while offering the comforts and technology of modern hospitality.</p>

        <h2>Bringing History Back to Life: BHG in Downtown</h2>
        
        <div class="property-showcase">
          <div class="property-item">
            <img src="/src/assets/kasr-living-property.png" alt="Kasr Living, Tahrir Square interior" class="property-image" />
            <div class="property-content">
              <h3>Kasr Living, Tahrir Square</h3>
              <p>A revitalized residence that blends classic architecture with stylish interiors, just steps away from the Egyptian Museum.</p>
            </div>
          </div>
          
          <div class="property-item">
            <img src="/src/assets/adly-apartments-property.png" alt="Art Deco Designer Apartments on Adly Street" class="property-image" />
            <div class="property-content">
              <h3>Hannaux and Grey Studios</h3>
              <p>Thoughtfully designed serviced apartments in Talaat Harb and Adly districts, where heritage façades conceal modern interiors tailored to today's urban traveler.</p>
            </div>
          </div>
        </div>
        
        <p>Each property reflects BHG's philosophy: preserving the architectural fabric while introducing operational excellence, design quality, and hospitality-driven amenities.</p>

        <h2>More Than Buildings: A Catalyst for Urban Revival</h2>
        
        <p>The impact of adaptive reuse extends far beyond individual hospitality projects. By investing strategically in Downtown, operators and developers contribute to the area's broader cultural and economic renaissance. Neglected structures become vibrant community assets once again, international guests and local residents are drawn back to the city center, and restaurants, shops, and creative industries benefit from increased foot traffic. Arts, entertainment, and cultural activities flourish in revitalized spaces.</p>
        
        <p>As Cairo continues to grow and evolve, Downtown remains its beating heart—a place where past and future converge. Adaptive reuse is proving to be the key to unlocking the district's vast potential, offering a sustainable strategy that protects history while actively shaping the future.</p>
        
        <p>For BHG, this approach represents more than just a business model; it embodies a deep commitment to redefining how people experience urban hospitality in one of the world's most historically significant cities.</p>
      `,
      date: "2024-01-25",
      image: "/public/lovable-uploads/567581b3-8bd4-4267-aeb1-5c4f86035dff.png",
      author: "BHG Editorial Team",
      readTime: "8 min read",
      category: "Urban Development"
    },
    {
      id: "2",
      type: "blog",
      title: "The Future of Luxury Real Estate in Egypt",
      excerpt: "Exploring emerging trends and opportunities in Egypt's luxury property market and what they mean for investors.",
      content: `
        <p>The luxury real estate market in Egypt is experiencing unprecedented growth, driven by economic reforms and increased foreign investment. Our analysis reveals key trends that are shaping the future of this dynamic sector.</p>
        
        <h2>Market Dynamics</h2>
        <p>Egypt's strategic location, rich cultural heritage, and recent economic reforms have positioned it as an attractive destination for luxury real estate investment. The government's Vision 2030 initiative has created a favorable environment for development, particularly in the luxury segment.</p>
        
        <h2>Key Investment Opportunities</h2>
        <p>Several factors are driving growth in the luxury real estate market:</p>
        <ul>
          <li>New Administrative Capital development</li>
          <li>Coastal developments along the Red Sea and Mediterranean</li>
          <li>Historic district renovations in Cairo and Alexandria</li>
          <li>Smart city initiatives incorporating cutting-edge technology</li>
        </ul>
        
        <h2>Technology Integration</h2>
        <p>Modern luxury developments are increasingly incorporating smart home technology, sustainable design principles, and advanced security systems. These features are becoming standard expectations rather than premium add-ons.</p>
        
        <h2>Future Outlook</h2>
        <p>The outlook for Egypt's luxury real estate market remains positive, with continued foreign investment and domestic demand driving growth. Developers who can deliver quality, innovation, and value will be well-positioned to capitalize on these opportunities.</p>
      `,
      date: "2024-01-20",
      image: "/public/lovable-uploads/567581b3-8bd4-4267-aeb1-5c4f86035dff.png",
      author: "Market Research Team",
      readTime: "5 min read",
      category: "Market Insights"
    },
    {
      id: "3",
      type: "blog",
      title: "Smart Technology Integration in Modern Hospitality",
      excerpt: "How cutting-edge technology is revolutionizing guest experiences and operational efficiency in luxury hotels.",
      content: `
        <p>The hospitality industry is undergoing a digital transformation that is fundamentally changing how guests experience luxury accommodations. From AI-powered concierge services to IoT-enabled room controls, technology is enhancing every aspect of the guest journey.</p>
        
        <h2>Digital Guest Experience</h2>
        <p>Modern luxury hotels are embracing technology to create seamless, personalized experiences. Digital check-in processes, mobile room keys, and app-controlled amenities are becoming standard features that guests expect.</p>
        
        <h2>Smart Room Technology</h2>
        <p>Today's luxury hotel rooms feature integrated technology systems that allow guests to control:</p>
        <ul>
          <li>Lighting and ambiance settings</li>
          <li>Climate control and air quality</li>
          <li>Entertainment systems and streaming services</li>
          <li>Window treatments and privacy controls</li>
          <li>Voice-activated concierge services</li>
        </ul>
        
        <h2>Operational Efficiency</h2>
        <p>Behind the scenes, technology is streamlining operations through predictive maintenance, energy management systems, and data analytics that help optimize everything from staffing to inventory management.</p>
        
        <h2>Sustainability Through Technology</h2>
        <p>Smart technology also plays a crucial role in sustainability efforts, with automated systems that optimize energy usage, reduce waste, and monitor environmental impact in real-time.</p>
        
        <h2>The Future of Hotel Technology</h2>
        <p>As technology continues to evolve, we expect to see even more innovative applications, including virtual reality experiences, biometric security systems, and AI-powered personalization that anticipates guest needs before they're expressed.</p>
      `,
      date: "2024-01-12",
      image: "/public/lovable-uploads/678bf767-6712-45c9-bda5-33d08d7e1a09.png",
      author: "Technology Team",
      readTime: "7 min read",
      category: "Technology"
    },
    {
      id: "4",
      type: "blog",
      title: "Sustainable Development: Building for Tomorrow",
      excerpt: "Our commitment to environmental responsibility and sustainable practices in all development projects.",
      content: `
        <p>Sustainability is no longer optional in real estate development. Environmental responsibility has become a core requirement for modern luxury developments, driven by both regulatory requirements and changing consumer expectations.</p>
        
        <h2>Green Building Practices</h2>
        <p>Our commitment to sustainability begins with the foundation of every project. We incorporate green building practices throughout the development process, from site selection and design to construction and operation.</p>
        
        <h2>Energy Efficiency</h2>
        <p>Modern sustainable developments feature:</p>
        <ul>
          <li>Solar panel installations and renewable energy systems</li>
          <li>High-efficiency HVAC systems with smart controls</li>
          <li>LED lighting and automated lighting management</li>
          <li>Energy-efficient appliances and fixtures</li>
          <li>Building management systems that optimize energy usage</li>
        </ul>
        
        <h2>Water Conservation</h2>
        <p>Water scarcity is a growing concern globally, making water conservation a critical component of sustainable development. Our projects incorporate advanced water management systems, including rainwater harvesting, greywater recycling, and drought-resistant landscaping.</p>
        
        <h2>Sustainable Materials</h2>
        <p>We prioritize the use of sustainable, locally-sourced materials that reduce environmental impact while supporting local economies. This includes recycled materials, low-emission products, and materials with minimal environmental footprint.</p>
        
        <h2>Long-term Benefits</h2>
        <p>Sustainable development practices not only benefit the environment but also provide long-term economic advantages through reduced operational costs, increased property values, and enhanced marketability to environmentally conscious buyers and tenants.</p>
      `,
      date: "2024-01-08",
      image: "/public/lovable-uploads/ac120a0f-ee3b-4173-820c-147325f30973.png",
      author: "Sustainability Team",
      readTime: "6 min read",
      category: "Sustainability"
    }
  ];

  // News articles
  const newsArticles = [
    {
      id: "1",
      type: "news",
      title: "Brassbell Holdings Expands Operations in Egypt",
      excerpt: "New developments planned across Cairo and Alexandria to meet growing demand for luxury accommodations.",
      content: `
        <p>Brassbell Holdings has announced a major expansion of operations across Egypt, with ambitious new luxury developments planned in prime locations throughout Cairo and Alexandria. This strategic growth initiative represents a significant investment in the Egyptian real estate market and underscores the company's confidence in the region's economic future.</p>
        
        <h2>Strategic Expansion Plan</h2>
        <p>The expansion encompasses multiple high-end residential and commercial projects, targeting the growing demand for luxury accommodations in Egypt's major urban centers. Key developments include:</p>
        <ul>
          <li>Luxury residential complexes in New Cairo</li>
          <li>Premium aparthotels in Alexandria's waterfront district</li>
          <li>Mixed-use developments integrating retail and hospitality</li>
          <li>Smart building initiatives incorporating cutting-edge technology</li>
        </ul>
        
        <h2>Market Opportunity</h2>
        <p>Egypt's real estate market has shown remarkable resilience and growth potential, driven by government initiatives, infrastructure development, and increasing foreign investment. The luxury segment, in particular, has experienced unprecedented demand from both domestic and international clients.</p>
        
        <h2>Investment Impact</h2>
        <p>This expansion is expected to create thousands of jobs and contribute significantly to the local economy. The developments will incorporate sustainable building practices and advanced technology systems, setting new standards for luxury living in Egypt.</p>
        
        <h2>Timeline and Delivery</h2>
        <p>The first phase of developments is scheduled to begin construction in Q2 2024, with initial deliveries expected by late 2025. The complete portfolio of projects will be delivered over a five-year timeline, ensuring quality and attention to detail throughout the development process.</p>
      `,
      date: "2024-01-15",
      image: "/public/lovable-uploads/069107c1-542f-4e64-9644-b6f0e605058c.png",
      author: "Business Development Team",
      publication: "Brassbell Holdings",
      category: "Expansion"
    },
    {
      id: "3",
      type: "news", 
      title: "Technology Integration in Smart Homes",
      excerpt: "Pioneering smart home technology implementation across all residential developments.",
      content: `
        <p>Brassbell Holdings is at the forefront of smart home technology integration, implementing cutting-edge systems across all residential developments to enhance living experiences and operational efficiency. This comprehensive technology initiative positions the company as a leader in modern residential solutions.</p>
        
        <h2>Smart Home Features</h2>
        <p>Our residential developments now feature comprehensive smart home systems that include:</p>
        <ul>
          <li>Integrated home automation and control systems</li>
          <li>Advanced security and monitoring technologies</li>
          <li>Energy management and optimization platforms</li>
          <li>Voice-activated assistants and IoT connectivity</li>
          <li>Mobile app integration for remote management</li>
        </ul>
        
        <h2>Technology Partnerships</h2>
        <p>We have established strategic partnerships with leading technology providers to ensure our residents have access to the latest innovations in home automation, security, and energy efficiency.</p>
        
        <h2>Sustainability Focus</h2>
        <p>The smart home systems are designed with sustainability in mind, featuring energy-efficient components, automated climate control, and real-time monitoring capabilities that help reduce environmental impact while lowering utility costs.</p>
        
        <h2>Future Innovations</h2>
        <p>Looking ahead, we continue to explore emerging technologies including AI-powered predictive systems, advanced biometric security, and integration with smart city infrastructure to create truly connected living environments.</p>
      `,
      date: "2024-01-05",
      image: "/public/lovable-uploads/311b8a25-3d03-42e7-b845-82a6008a5135.png",
      author: "Technology Division",
      publication: "Brassbell Holdings",
      category: "Innovation"
    }
  ];

  // Press articles
  const pressArticles = [
    {
      id: "2",
      type: "press",
      title: "Industry Recognition for Sustainable Development", 
      excerpt: "Awarded for innovative approach to sustainable hospitality and residential projects.",
      content: `
        <p>Brassbell Holdings has been honored with the prestigious Sustainable Development Excellence Award by the Egyptian Real Estate Council, recognizing the company's innovative approach to environmental responsibility in both hospitality and residential projects.</p>
        
        <h2>Award Recognition</h2>
        <p>The award acknowledges Brassbell Holdings' commitment to sustainable development practices, including:</p>
        <ul>
          <li>Implementation of green building standards across all projects</li>
          <li>Integration of renewable energy systems</li>
          <li>Water conservation and waste reduction initiatives</li>
          <li>Use of sustainable materials and construction methods</li>
          <li>Community engagement and environmental education programs</li>
        </ul>
        
        <h2>Industry Impact</h2>
        <p>The Egyptian Real Estate Council noted that Brassbell Holdings has set new benchmarks for sustainable development in the luxury real estate sector, inspiring other developers to adopt similar practices.</p>
        
        <h2>Environmental Commitment</h2>
        <p>This recognition reflects our long-standing commitment to environmental stewardship and our belief that luxury and sustainability can coexist. Our developments demonstrate that high-end projects can achieve exceptional environmental performance without compromising on quality or amenities.</p>
        
        <h2>Future Initiatives</h2>
        <p>Building on this recognition, we continue to invest in research and development of new sustainable technologies and practices that will further reduce our environmental footprint while enhancing the living and hospitality experiences we provide.</p>
      `,
      date: "2024-01-10",
      image: "/public/lovable-uploads/2569221e-3fd7-4f73-8082-24b12bb2e55d.png",
      author: "Egyptian Real Estate Council",
      publication: "Egyptian Real Estate Journal",
      category: "Awards"
    },
    {
      id: "4",
      type: "press",
      title: "Partnership Announcement with Global Hospitality Brand",
      excerpt: "Strategic partnership to enhance service offerings and expand market reach.",
      content: `
        <p>Brassbell Holdings is pleased to announce a strategic partnership with a leading global hospitality brand that will significantly enhance service offerings and expand market reach throughout the Middle East region. This collaboration represents a major milestone in the company's growth strategy.</p>
        
        <h2>Partnership Benefits</h2>
        <p>The strategic alliance will provide numerous advantages:</p>
        <ul>
          <li>Access to world-class hospitality management expertise</li>
          <li>Enhanced service standards across all properties</li>
          <li>Expanded distribution and marketing networks</li>
          <li>Advanced technology and operational systems</li>
          <li>International brand recognition and credibility</li>
        </ul>
        
        <h2>Market Expansion</h2>
        <p>This partnership positions Brassbell Holdings to accelerate expansion across key Middle Eastern markets, leveraging the partner's established presence and reputation in the global hospitality sector.</p>
        
        <h2>Service Enhancement</h2>
        <p>Guests at Brassbell Holdings properties will benefit from elevated service standards, enhanced amenities, and access to a broader network of hospitality services and partnerships worldwide.</p>
        
        <h2>Future Growth</h2>
        <p>The partnership creates a foundation for sustainable growth and positions both companies to capitalize on emerging opportunities in the rapidly evolving hospitality market in the Middle East and North Africa region.</p>
      `,
      date: "2023-12-28",
      image: "/public/lovable-uploads/3be87d2e-f927-4fbf-baab-197f6f3b071a.png",
      author: "Partnership Team",
      publication: "Hospitality News Middle East",
      category: "Partnerships"
    }
  ];

  // Combine all articles
  const allArticles = [...blogPosts, ...newsArticles, ...pressArticles];
  
  // Get the article type from the URL
  const path = window.location.pathname;
  const articleType = path.split('/')[1]; // 'blog', 'news', or 'press'
  
  const article = allArticles.find(post => post.id === id && post.type === articleType);

  const handleShare = async () => {
    const url = window.location.href;
    const title = article?.title || 'Article';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: url,
        });
      } catch (error) {
        // User cancelled sharing or error occurred
        console.log('Sharing cancelled or failed');
      }
    } else {
      // Fallback to copying URL to clipboard
      try {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied!",
          description: "Article link has been copied to clipboard.",
        });
      } catch (error) {
        toast({
          title: "Share failed",
          description: "Unable to copy link to clipboard.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSave = () => {
    if (!article) return;
    
    const savedArticles = JSON.parse(localStorage.getItem('savedArticles') || '[]');
    const isAlreadySaved = savedArticles.some((saved: any) => saved.id === article.id && saved.type === article.type);
    
    if (isAlreadySaved) {
      toast({
        title: "Already saved",
        description: "This article is already in your saved list.",
      });
    } else {
      savedArticles.push({
        id: article.id,
        type: article.type,
        title: article.title,
        excerpt: article.excerpt,
        date: article.date,
        image: article.image,
        savedAt: new Date().toISOString()
      });
      localStorage.setItem('savedArticles', JSON.stringify(savedArticles));
      toast({
        title: "Article saved!",
        description: "Added to your reading list.",
      });
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-16 text-center">
            <h1 className="text-2xl font-semibold mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist.</p>
            <Link to="/news-press">
              <Button>Back to News & Press</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        {/* Back Navigation */}
        <div className="container mx-auto px-4 py-6">
          <Link 
            to={articleType === 'blog' ? "/news-press?tab=blog" : "/news-press"}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {articleType === 'blog' ? 'Blog' : 'News & Press'}
          </Link>
        </div>

        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
          
          <div className="container mx-auto px-4 py-12">
            <div className="max-w-4xl mx-auto">
              {/* Featured Image */}
              <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 shadow-2xl">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Header */}
              <div className="space-y-6">
                {/* Category and Meta */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="px-3 py-1 bg-primary/10 text-primary rounded-full font-medium">
                    {article.category}
                  </span>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {new Date(article.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  {(article as any).readTime && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {(article as any).readTime}
                    </div>
                  )}
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-foreground leading-tight">
                  {article.title}
                </h1>

                {/* Author and Actions */}
                <div className="flex items-center justify-between flex-wrap gap-4 pb-8 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{article.author}</p>
                      <p className="text-sm text-muted-foreground">
                        {(article as any).publication ? (article as any).publication : 'Author'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-2" onClick={handleShare}>
                      <Share2 className="h-4 w-4" />
                      Share
                    </Button>
                    <Button variant="outline" size="sm" className="gap-2" onClick={handleSave}>
                      <BookOpen className="h-4 w-4" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: article.content }}
                style={{
                  '--tw-prose-body': 'hsl(var(--foreground))',
                  '--tw-prose-headings': 'hsl(var(--foreground))',
                  '--tw-prose-links': 'hsl(var(--primary))',
                  '--tw-prose-bold': 'hsl(var(--foreground))',
                  '--tw-prose-counters': 'hsl(var(--muted-foreground))',
                  '--tw-prose-bullets': 'hsl(var(--muted-foreground))',
                  '--tw-prose-hr': 'hsl(var(--border))',
                  '--tw-prose-quotes': 'hsl(var(--foreground))',
                  '--tw-prose-quote-borders': 'hsl(var(--border))',
                  '--tw-prose-captions': 'hsl(var(--muted-foreground))',
                  '--tw-prose-code': 'hsl(var(--foreground))',
                  '--tw-prose-pre-code': 'hsl(var(--muted-foreground))',
                  '--tw-prose-pre-bg': 'hsl(var(--muted))',
                  '--tw-prose-th-borders': 'hsl(var(--border))',
                  '--tw-prose-td-borders': 'hsl(var(--border))',
                } as React.CSSProperties}
              />
              
              {/* Call to Action */}
              <div className="mt-16 p-8 bg-muted/50 rounded-2xl text-center">
                <h3 className="text-xl font-semibold mb-4">Stay Updated</h3>
                <p className="text-muted-foreground mb-6">
                  Subscribe to our newsletter for the latest insights and industry updates.
                </p>
                <Button className="gap-2">
                  Subscribe Now
                  <ArrowLeft className="h-4 w-4 rotate-180" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 bg-muted/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-light mb-8">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {allArticles
                  .filter(post => post.id !== id)
                  .slice(0, 2)
                  .map((post) => (
                    <Link 
                      key={post.id}
                      to={`/${post.type}/${post.id}`}
                      className="group"
                    >
                      <article className="bg-background rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                              {post.category}
                            </span>
                            {(post as any).readTime && <span>{(post as any).readTime}</span>}
                          </div>
                          <h3 className="text-lg font-medium group-hover:text-primary transition-colors mb-2">
                            {post.title}
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            {post.excerpt}
                          </p>
                        </div>
                      </article>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default BlogPost;